import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ContactUs } from './../../shared/services/contact-us/contact-us';
import { ContactUsService } from './../../shared/services/contact-us/contact-us.service';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ErrorComponent, InformationComponent } from 'src/app/shared/util/helpers';
import { filter, finalize } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { TrackerService } from 'src/app/shared/util/tracker.service';
import { environment } from 'src/environments/environment';

export interface DeploymentOption {
  image: string;
  titleKey: string;
  textKey: string;
}

/** Sticky header (~4rem) + small gap */
const SCROLL_HEADER_OFFSET = 80;

@Component({
  selector: 'app-solution-personalised',
  templateUrl: './solution-personalised.component.html',
  styleUrls: ['./solution-personalised.component.scss']
})
export class SolutionPersonalisedComponent implements OnInit, AfterViewInit, OnDestroy {
  contactItem: ContactUs = new ContactUs();
  assistantHref = '';
  isLoading = false;
  contactSuccess = false;

  private routerEventsSub: Subscription | null = null;
  private fragmentScrollTimer: ReturnType<typeof setTimeout> | null = null;

  readonly deploymentOptions: DeploymentOption[] = [
    {
      image: 'assets/images/marketing/S-Personalized-Cloud.svg',
      titleKey: 'PERSONALIZED_SOLUTION.SOLUTION1_TITLE',
      textKey: 'PERSONALIZED_SOLUTION.SOLUTION1_SUBTITLE',
    },
    {
      image: 'assets/images/marketing/S-Personalized-WhiteLabel-Logo.svg',
      titleKey: 'PERSONALIZED_SOLUTION.SOLUTION2_TITLE',
      textKey: 'PERSONALIZED_SOLUTION.SOLUTION2_SUBTITLE',
    },
    {
      image: 'assets/images/marketing/S-Personalized-Db-Cloud.svg',
      titleKey: 'PERSONALIZED_SOLUTION.SOLUTION3_TITLE',
      textKey: 'PERSONALIZED_SOLUTION.SOLUTION3_SUBTITLE',
    },
    {
      image: 'assets/images/marketing/S-Personalized-Db.svg',
      titleKey: 'PERSONALIZED_SOLUTION.SOLUTION4_TITLE',
      textKey: 'PERSONALIZED_SOLUTION.SOLUTION4_SUBTITLE',
    },
  ];

  constructor(
    public snackBar: MatSnackBar,
    public contactUsService: ContactUsService,
    private router: Router,
    public translate: TranslateService,
    private trackerService: TrackerService
  ) {}

  ngOnInit(): void {
    this.trackerService.trackPageViews();
    this.assistantHref = environment.assistantApp;

    this.contactItem.subject = '';
    this.contactItem.content = '';
    this.contactItem.email = '';
    this.contactItem.telephone = '';

    this.routerEventsSub = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => {
        const fragment = this.getUrlFragment();
        if (fragment) {
          this.scheduleScrollToFragment(fragment);
        }
      });

    const initialFragment = this.getUrlFragment();
    if (initialFragment) {
      this.scheduleScrollToFragment(initialFragment);
    }
  }

  ngAfterViewInit(): void {
    const fragment = this.getUrlFragment();
    if (fragment) {
      this.scheduleScrollToFragment(fragment);
    }
  }

  ngOnDestroy(): void {
    this.routerEventsSub?.unsubscribe();
    if (this.fragmentScrollTimer) {
      clearTimeout(this.fragmentScrollTimer);
    }
  }

  send(): void {
    if (!this.contactItem.subject || !this.contactItem.email || !this.contactItem.content) {
      this.snackBar.openFromComponent(ErrorComponent, {
        data: { message: 'Vă rugăm să completați toate câmpurile.' },
        duration: 2000,
      });
      return;
    }

    this.isLoading = true;
    this.contactSuccess = false;
    this.contactItem.date = new Date();

    this.contactUsService
      .addItem(this.contactItem)
      .pipe(finalize(() => {
        this.isLoading = false;
      }))
      .subscribe(
        () => {
          this.contactItem.subject = '';
          this.contactItem.email = '';
          this.contactItem.content = '';
          this.contactSuccess = true;
          this.snackBar.openFromComponent(InformationComponent, {
            data: { message: 'Mesajul a fost trimis cu succes!' },
            duration: 2000,
          });
        },
        () => {
          this.snackBar.openFromComponent(ErrorComponent, {
            data: { message: 'A apărut o eroare. Vă rugăm să încercați din nou.' },
            duration: 2000,
          });
        }
      );
  }

  private getUrlFragment(): string | null {
    return this.router.parseUrl(this.router.url).fragment;
  }

  private scheduleScrollToFragment(fragment: string): void {
    if (this.fragmentScrollTimer) {
      clearTimeout(this.fragmentScrollTimer);
    }
    this.fragmentScrollTimer = setTimeout(() => this.scrollToFragment(fragment, 0), 50);
  }

  private scrollToFragment(fragment: string, attempt: number): void {
    const target = document.getElementById(fragment);
    if (target) {
      const top =
        target.getBoundingClientRect().top + window.pageYOffset - SCROLL_HEADER_OFFSET;
      window.scrollTo({
        top: Math.max(0, top),
        behavior: attempt === 0 ? 'smooth' : 'auto',
      });
      return;
    }

    if (attempt < 20) {
      setTimeout(() => this.scrollToFragment(fragment, attempt + 1), 100);
    }
  }
}
